import React, { useState } from 'react';
import { Crown, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { dynastyTheme } from '../../../services/colorService';
import { mobilePageBackground } from '../../../services/mobileStyleService';
import { getBaseballBackground } from './baseballAssets';

const MobileSignIn = ({ onSignIn, onSwitchToSignUp, onForgotPassword, loading, error }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSignIn(formData.email, formData.password);
  };

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ background: mobilePageBackground, ...getBaseballBackground() }}
    >
      {/* Header Section */}
      <div className="flex-shrink-0 flex flex-col items-center justify-center pt-16 pb-8 px-6">
        {/* Baseball decorative line */}
        <div className="flex items-center gap-4 mb-6">
          <span className="text-2xl">⚾</span>
          <div className={`h-px w-12 ${dynastyTheme.classes.bg.primary}`}></div>
          <div className={`h-px w-12 ${dynastyTheme.classes.bg.primary}`}></div>
          <span className="text-2xl">⚾</span>
        </div>

        {/* Crown + Title */}
        <div className="flex items-center gap-3 mb-3">
          <Crown className={`w-8 h-8 ${dynastyTheme.classes.text.primary}`} />
          <h1 className={`text-3xl font-bold ${dynastyTheme.classes.text.white}`}>
            Dynasty Dugout
          </h1>
        </div>

        {/* Tagline */}
        <p className={`text-base ${dynastyTheme.classes.text.neutralLighter} text-center flex items-center gap-2`}>
          <span>⚾</span>
          Build Your Championship Team
          <span>⚾</span>
        </p>
      </div>

      {/* Form Section */}
      <div className="flex-1 px-6 pb-8">
        <form onSubmit={handleSubmit} className="space-y-6 max-w-md mx-auto">
          {/* Error Message */}
          {error && (
            <div className={`${dynastyTheme.components.badge.error} p-4 rounded-lg text-center`}>
              {error}
            </div>
          )}

          {/* Email Input */}
          <div>
            <label className={dynastyTheme.components.label}>
              Email
            </label>
            <div className="relative">
              <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${dynastyTheme.classes.text.neutralLighter}`} />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`${dynastyTheme.components.input} w-full pl-12 pr-4 py-3 text-base`}
                placeholder="your@email.com"
                required
                autoComplete="email"
              />
            </div>
          </div>

          {/* Password Input */}
          <div>
            <label className={dynastyTheme.components.label}>
              Password
            </label>
            <div className="relative">
              <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${dynastyTheme.classes.text.neutralLighter}`} />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`${dynastyTheme.components.input} w-full pl-12 pr-12 py-3 text-base`}
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 ${dynastyTheme.classes.text.neutralLighter} hover:${dynastyTheme.classes.text.white} ${dynastyTheme.classes.transition}`}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Sign In Button */}
          <button
            type="submit"
            disabled={loading}
            className={`${dynastyTheme.utils.getComponent('button', 'primary', 'lg')} w-full flex items-center justify-center gap-2`}
          >
            {loading ? (
              <>Signing In...</>
            ) : (
              <>
                <span>⚾</span>
                SIGN IN
                <span>⚾</span>
              </>
            )}
          </button>

          {/* Forgot Password Link */}
          <button
            type="button"
            onClick={onForgotPassword}
            className={`w-full text-sm ${dynastyTheme.classes.text.neutralLighter} hover:${dynastyTheme.classes.text.primary} ${dynastyTheme.classes.transition} text-center`}
          >
            Forgot your password?
          </button>
        </form>
      </div>

      {/* Footer - Sign Up CTA */}
      <div className={`flex-shrink-0 border-t ${dynastyTheme.classes.border.neutral} px-6 py-6 text-center`}>
        <p className={`text-sm ${dynastyTheme.classes.text.neutralLighter} mb-3`}>
          Don't have an account?
        </p>
        <button
          onClick={onSwitchToSignUp}
          className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'lg')} w-full max-w-md mx-auto flex items-center justify-center gap-2`}
        >
          <span>🏟️</span>
          Join the League
          <span>→</span>
        </button>
      </div>
    </div>
  );
};

export default MobileSignIn;
